import { ChangeEvent } from "react";
import { FieldArray, useFormikContext } from "formik";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { PlusIcon } from "components/icons/PlusIcon";
import { Trash20 } from "components/icons/Trash20";
import { PhysicalNexusStateRowFormValues } from "./PhysicalNexusTrackerModal";

const PHYSICAL_PRESENCE_COLUMNS = ["Office", "Warehouse", "Employee"] as const;
const CHECKBOX_COLUMN_WIDTH = "t-w-20";

type PresenceField = "office" | "warehouse" | "employee";

const toPresenceField = (
  column: (typeof PHYSICAL_PRESENCE_COLUMNS)[number]
): PresenceField => column.toLowerCase() as PresenceField;

const EMPTY_STATE_ROW: PhysicalNexusStateRowFormValues = {
  state: "",
  office: false,
  warehouse: false,
  employee: false,
};

type PhysicalNexusFormValues = {
  states: PhysicalNexusStateRowFormValues[];
};

type StateRowProps = {
  index: number;
  stateOptions?: Array<{ value: string; label: string }>;
  onRemove: (index: number) => void;
  lockStateSelection?: boolean;
};

const StateRow = ({
  index,
  stateOptions,
  onRemove,
  lockStateSelection,
}: StateRowProps) => {
  const { values, setFieldValue } = useFormikContext<PhysicalNexusFormValues>();
  const row = values.states[index];

  const handleRemove = () => onRemove(index);

  const togglePresence = (field: PresenceField) =>
    setFieldValue(`states.${index}.${field}`, !row[field]);

  const selectedStates = new Set(
    values.states.map((item, itemIndex) =>
      itemIndex === index ? null : item.state
    )
  );
  const availableStateOptions = stateOptions?.filter(
    (option) => !selectedStates.has(option.value)
  );
  const selectedStateOption = stateOptions?.find(
    (option) => option.value === row.state
  );

  return (
    <div className="t-flex t-items-center t-gap-3 sm:t-flex-wrap sm:t-rounded-lg sm:t-border sm:t-border-solid sm:t-border-neutral-0 sm:t-p-3">
      <div className="t-flex-1">
        <Combobox
          withForm
          name={`states.${index}.state`}
          placeholder="Select state"
          options={availableStateOptions}
          value={selectedStateOption || null}
          menuPortalTarget={document.body}
          isDisabled={lockStateSelection}
        />
      </div>

      {PHYSICAL_PRESENCE_COLUMNS.map((column) => {
        const field = toPresenceField(column);
        const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
          setFieldValue(`states.${index}.${field}`, e.target.checked);

        return (
          <div
            key={column}
            className={`t-grid t-place-items-center ${CHECKBOX_COLUMN_WIDTH} sm:t-hidden`}
          >
            <Checkbox
              checked={row[field]}
              onChange={handleChange}
              disabled={!row.state}
            />
          </div>
        );
      })}

      {!lockStateSelection && (
        <Button customType="ghost_icon" size="small" onClick={handleRemove}>
          <span className="t-text-text-30">
            <Trash20 />
          </span>
        </Button>
      )}

      <div className="t-hidden sm:t-flex sm:t-basis-full sm:t-flex-wrap sm:t-gap-2">
        {PHYSICAL_PRESENCE_COLUMNS.map((column) => {
          const field = toPresenceField(column);
          const handleToggle = () => togglePresence(field);

          return (
            <Button
              key={column}
              type="button"
              size="small"
              customType={row[field] ? "primary_outlined" : "secondary"}
              disabled={!row.state}
              onClick={handleToggle}
            >
              {column}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export const PhysicalNexusTrackerModalBody = ({
  stateOptions,
  lockStateSelection,
}: {
  stateOptions?: Array<{ value: string; label: string }>;
  lockStateSelection?: boolean;
}) => {
  const { values } = useFormikContext<PhysicalNexusFormValues>();

  return (
    <Modal.Body className="sm:t-px-4">
      <p className="t-text-body-sm t-text-text-60 t-mb-4 t-mt-0">
        Tell us which states you have an office, a warehouse, or employees in
        the U.S. We use this to work out where you need to register for state
        taxes.
      </p>

      <FieldArray name="states">
        {({ remove, push }) => (
          <div className="t-flex t-flex-col t-gap-3">
            <div className="t-flex t-items-center t-gap-3 t-bg-surface-lighter-grey t-px-3 t-py-2 t-rounded sm:t-hidden">
              <Label className="t-flex-1 t-mb-0">State</Label>
              {PHYSICAL_PRESENCE_COLUMNS.map((column) => (
                <Label
                  key={column}
                  className={`t-text-center t-mb-0 ${CHECKBOX_COLUMN_WIDTH}`}
                >
                  {column}
                </Label>
              ))}
              {!lockStateSelection && <div className="t-w-6" />}
            </div>

            {values.states.map((row, index) => (
              <StateRow
                key={row.state || `new-row-${index}`}
                index={index}
                stateOptions={stateOptions}
                onRemove={remove}
                lockStateSelection={lockStateSelection}
              />
            ))}

            {!lockStateSelection && (
              <div>
                <Button
                  customType="secondary"
                  size="small"
                  type="button"
                  onClick={() => push({ ...EMPTY_STATE_ROW })}
                >
                  <span className="t-flex t-items-center t-gap-1">
                    <PlusIcon />
                    <span>Add State</span>
                  </span>
                </Button>
              </div>
            )}
          </div>
        )}
      </FieldArray>
    </Modal.Body>
  );
};
